<template>
  <div
    class="coupon-card-wrapper"
    :class="{ 'is-selected': isSelected, 'is-locked': isLocked, 'is-browse': isBrowseMode, 'is-cart': isCartMode, 'is-history': isHistoryItem }"
    @click="handleCardClick"
  >
    <div class="visual-wrapper">
      <img :src="couponImage" alt="coupon-image" class="coupon-image">
    </div>

    <div class="content-wrapper">
      <div v-if="isCartMode" class="content-top content-top-cart">
        <p class="header header-cart">
          {{ displayName }}
        </p>
      </div>
      <template v-else>
      <div class="content-top">
        <div class="top-meta-row">
          <div class="chip-row">
            <p class="badge" :class="badgeClass">
              {{ badgeLabel }}
            </p>
            <p class="state-chip" :class="stateChipClass">
              {{ stateLabel }}
            </p>
          </div>
          <button
            v-if="isBrowseMode"
            class="detail-link"
            type="button"
            @click.stop="handleCardClick"
          >
            {{ t('myCoupon.card.viewDetails') }}
          </button>
        </div>

        <p class="header">
          {{ displayName }}
        </p>

        <p class="subtitle">
          {{ subtitleLabel }}
        </p>

        <div class="meta-row">
          <p v-if="recentLinkDaysLeftLabel" class="meta-pill is-days-left">
            {{ recentLinkDaysLeftLabel }}
          </p>
          <p v-if="scopeLabel" class="meta-pill">
            {{ scopeLabel }}
          </p>
          <p v-if="minLabel" class="meta-pill">
            {{ minLabel }}
          </p>
          <p v-if="usageLimitLabel" class="meta-pill">
            {{ usageLimitLabel }}
          </p>
        </div>

        <p v-if="detailLabel" class="detail">
          {{ detailLabel }}
        </p>

        <p v-if="isLocked && coupon.lock_reason" class="lock-reason">
          {{ coupon.lock_reason }}
        </p>

        <p v-if="clinicBenefitCodeLabel" class="clinic-code">
          {{ clinicBenefitCodeLabel }}
        </p>
      </div>

      <div class="content-bottom">
        <p class="expired" v-if="displayExpired">
          {{ t('myCoupon.card.expired') }} {{ displayExpired }}
        </p>
        <p v-else-if="historyTimestampLabel" class="expired">
          {{ historyTimestampLabel }}
        </p>
        <p v-else class="expired">
          {{ t('myCoupon.card.noExpired') }}
        </p>

        <button
          v-if="showActionButton"
          class="action-btn"
          :class="{
            locked: isLocked,
            primary: isPointsRedeemable,
            free: isFreeRedeemable,
            secondary: isOwnedOrPublic
          }"
          :disabled="isLocked"
          @click.stop="handleAction"
        >
          {{ actionLabel }}
        </button>
      </div>
      </template>
    </div>

    <div
      v-if="showCheck"
      class="check-wrapper"
      :class="{ 'is-selected': isSelected }"
    >
      <img src="@/assets/icons/small-check.svg" alt="selected">
    </div>
  </div>
</template>

<script>
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import dayjs from 'dayjs'

export default {
  emits: ['action', 'detail'],
  props: {
    coupon: {
      type: Object,
      default: null,
    },
    mode: {
      type: String,
      default: 'cart',
    },
    noSelection: {
      type: Boolean,
      default: false,
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()

    const isHistoryItem = computed(() => props.coupon?.display_state === 'history')
    const displayExpired = computed(() => {
      if (isHistoryItem.value) {
        return null
      }
      const effectiveExpired = props.coupon?.owned_expires_at || props.coupon?.expired
      return effectiveExpired ? dayjs(effectiveExpired).format('DD/MM/YYYY') : null
    })
    const displayName = computed(() => {
      if (props.coupon?.display_name) {
        return props.coupon.display_name
      }
      return `${t('myCoupon.card.discount')} ${props.coupon.discount_amount}${props.coupon.discount_type === 'amount' ? '฿' : '%'}`
    })
    const couponImage = computed(() => (
      props.coupon?.picture_full || props.coupon?.picture_url || props.coupon?.picture_thumbnail
    ))
    const detailLabel = computed(() => props.coupon?.display_detail || '')
    const isSelected = computed(() => props.mode === 'cart' && props.coupon?.isSelected)
    const isBrowseMode = computed(() => props.mode === 'browse')
    const isCartMode = computed(() => props.mode === 'cart')
    const isLocked = computed(() => props.coupon?.display_state === 'locked')
    const isRedeemable = computed(() => props.coupon?.display_state === 'redeemable')
    const isFreeRedeemable = computed(() => isRedeemable.value && Number(props.coupon?.burn_coin || 0) === 0)
    const isPointsRedeemable = computed(() => isRedeemable.value && Number(props.coupon?.burn_coin || 0) > 0)
    const isOwnedOrPublic = computed(() => ['owned', 'public'].includes(props.coupon?.display_state))
    const isClinicBenefit = computed(() => props.coupon?.benefit_type === 'clinic_benefit')
    const isOwnedClinicBenefit = computed(() => isClinicBenefit.value && props.coupon?.display_state === 'owned')
    const showCheck = computed(() => !props.noSelection && props.mode === 'cart')
    const showActionButton = computed(() => isBrowseMode.value && !isHistoryItem.value)
    const badgeLabel = computed(() => {
      if (isClinicBenefit.value) {
        return t('myCoupon.card.badges.inStore')
      }
      if (props.coupon?.is_birthday_month_only) {
        return t('myCoupon.card.badges.birthday')
      }
      if (Number(props.coupon?.burn_coin || 0) > 0) {
        return t('myCoupon.card.badges.points')
      }
      return t('myCoupon.card.badges.coupon')
    })
    const badgeClass = computed(() => ({
      'is-in-store': isClinicBenefit.value,
      'is-birthday': props.coupon?.is_birthday_month_only,
      'is-points': Number(props.coupon?.burn_coin || 0) > 0 && !isClinicBenefit.value,
    }))
    const scopeLabel = computed(() => {
      if (props.coupon.is_birthday_month_only) {
        return t('myCoupon.card.birthdayOnly')
      }
      if (props.coupon.product_scope === 'mila_claire_only') {
        return t('myCoupon.card.milaClaireOnly')
      }
      if (props.coupon.product_scope === 'exclude_mila_claire') {
        return t('myCoupon.card.makalinOnly')
      }
      return ''
    })
    const minLabel = computed(() => (
      props.coupon.min_total > 0
        ? `${t('myCoupon.card.min')} ${props.coupon.min_total}฿`
        : ''
    ))
    const stateLabel = computed(() => {
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.clinicBenefitReady')
      }
      if (props.coupon?.display_state === 'owned') {
        return t('myCoupon.card.owned')
      }
      if (props.coupon?.display_state === 'history') {
        return props.coupon?.history_type === 'used'
          ? t('myCoupon.card.used')
          : t('myCoupon.card.expiredLabel')
      }
      if (props.coupon?.display_state === 'public') {
        return t('myCoupon.card.available')
      }
      if (props.coupon?.display_state === 'redeemable') {
        return isFreeRedeemable.value
          ? t('myCoupon.card.freeRedeemable')
          : t('myCoupon.card.pointsRedeemable')
      }
      if (props.coupon?.display_state === 'locked') {
        return t('myCoupon.card.locked')
      }
      return ''
    })
    const stateChipClass = computed(() => ({
      'is-locked': isLocked.value,
      'is-free': isFreeRedeemable.value,
      'is-points': isPointsRedeemable.value,
      'is-history': isHistoryItem.value,
      'is-owned': props.coupon?.display_state === 'owned',
      'is-public': props.coupon?.display_state === 'public',
      'is-clinic': isOwnedClinicBenefit.value,
    }))
    const subtitleLabel = computed(() => {
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.clinicRewardSubtitle')
      }
      if (isFreeRedeemable.value) {
        return t('myCoupon.card.freeRewardSubtitle')
      }
      if (isPointsRedeemable.value) {
        return t('myCoupon.card.pointsRewardSubtitle')
      }
      if (props.coupon?.display_state === 'owned') {
        return clinicBenefitLabel.value || t('myCoupon.card.visualReady')
      }
      if (props.coupon?.display_state === 'history') {
        return props.coupon?.history_type === 'used'
          ? t('myCoupon.card.historyUsed')
          : t('myCoupon.card.historyExpired')
      }
      return clinicBenefitLabel.value || detailLabel.value || t('myCoupon.card.visualReward')
    })
    const actionLabel = computed(() => {
      if (isLocked.value) {
        return t('myCoupon.card.locked')
      }
      if (isOwnedClinicBenefit.value) {
        return t('myCoupon.card.showQr')
      }
      if (isFreeRedeemable.value) {
        return t('myCoupon.card.claimFree')
      }
      if (isPointsRedeemable.value) {
        return t('myCoupon.card.redeemWithPoints')
      }
      return t('myCoupon.card.useInCart')
    })
    const clinicBenefitLabel = computed(() => {
      if (!isClinicBenefit.value) {
        return ''
      }
      return t('myCoupon.card.clinicBenefit')
    })
    const clinicBenefitCodeLabel = computed(() => {
      const issuedCode = props.coupon?.clinic_benefit_issue?.issued_code
      if (!issuedCode) {
        return ''
      }
      return t('myCoupon.card.clinicBenefitCode', [issuedCode])
    })
    const usageLimitLabel = computed(() => {
      if (props.coupon?.remaining_use_count === null || props.coupon?.remaining_use_count === undefined) {
        return ''
      }
      return t('myCoupon.card.remainingUse', [props.coupon.remaining_use_count])
    })
    // Recent-link coupons: countdown of days left in the user's eligibility window.
    // Backend sends recent_link_days_left (null when N/A, 0 when the window closed).
    const recentLinkDaysLeftLabel = computed(() => {
      const daysLeft = props.coupon?.recent_link_days_left
      if (daysLeft === null || daysLeft === undefined || daysLeft <= 0) {
        return ''
      }
      return t('myCoupon.card.recentLinkDaysLeft', [daysLeft])
    })
    const historyTimestampLabel = computed(() => {
      if (!isHistoryItem.value) {
        return ''
      }
      const historyAt = props.coupon?.history_at || props.coupon?.used_at || props.coupon?.expired_at
      if (!historyAt) {
        return ''
      }
      const formatted = dayjs(historyAt).format('DD/MM/YYYY HH:mm')
      return props.coupon?.history_type === 'used'
        ? t('myCoupon.card.usedOn', [formatted])
        : t('myCoupon.card.expiredOn', [formatted])
    })

    const handleAction = () => {
      if (isLocked.value) {
        return
      }
      emit('action', props.coupon)
    }

    const handleCardClick = () => {
      if (props.mode === 'cart') {
        emit('action', props.coupon)
        return
      }
      emit('detail', props.coupon)
    }

    return {
      t,
      displayName,
      couponImage,
      detailLabel,
      displayExpired,
      isSelected,
      isBrowseMode,
      isCartMode,
      isLocked,
      isHistoryItem,
      isFreeRedeemable,
      isPointsRedeemable,
      isOwnedOrPublic,
      showCheck,
      showActionButton,
      badgeLabel,
      badgeClass,
      scopeLabel,
      minLabel,
      recentLinkDaysLeftLabel,
      stateLabel,
      stateChipClass,
      subtitleLabel,
      clinicBenefitCodeLabel,
      usageLimitLabel,
      historyTimestampLabel,
      actionLabel,
      handleAction,
      handleCardClick,
    }
  }
}
</script>

<style lang="scss" scoped>
.coupon-card-wrapper {
  display: flex;
  align-items: stretch;
  gap: 12px;
  border: 1px solid #dbe3f2;
  border-radius: 24px;
  padding: 12px;
  background: rgba(255, 255, 255, 0.96);
  box-shadow: 0 12px 28px rgba(40, 59, 108, 0.08);
  transition: all 0.25s ease;
  cursor: pointer;

  &.is-selected {
    border-color: var(--success-color);
    box-shadow: 0 16px 32px rgba(76, 199, 100, 0.16);
  }

  &.is-locked {
    opacity: 0.72;
    cursor: default;
    background: #f6f7fb;
  }

  &.is-history {
    opacity: 0.7;
    background: #f5f6fa;
    box-shadow: none;

    .coupon-image {
      filter: grayscale(1);
    }
  }

  &.is-cart {
    align-items: stretch;
    min-height: 104px;
    .visual-wrapper {
      width: 104px;
      height: 104px;
      border-radius: 20px;
      align-self: stretch;
    }

    .content-wrapper {
      min-height: 104px;
      justify-content: center;
      gap: 0;
    }
  }
}

.visual-wrapper {
  width: 92px;
  height: 92px;
  flex-shrink: 0;
  border-radius: 18px;
  background: #eef2fb;
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: center;
  overflow: hidden;
}

.coupon-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
}

.content-wrapper {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 10px;
}

.content-top {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
}

.content-top-cart {
  gap: 0;
}

.top-meta-row {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
}

.chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  min-width: 0;
}

.badge,
.state-chip,
.meta-pill {
  border-radius: 999px;
  line-height: 1.2;
}

.badge {
  padding: 5px 9px;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #60708a;
  background: #eef2fb;

  &.is-in-store {
    background: rgba(28, 45, 236, 0.1);
    color: #1c2dec;
  }

  &.is-birthday {
    background: rgba(255, 105, 180, 0.12);
    color: #d0488b;
  }

  &.is-points {
    background: rgba(76, 199, 100, 0.12);
    color: #229a43;
  }
}

.state-chip {
  padding: 4px 9px;
  font-size: 10px;
  font-weight: 700;
  color: #44566c;
  background: #eef2fb;

  &.is-free {
    background: rgba(76, 199, 100, 0.14);
    color: #229a43;
  }

  &.is-points {
    background: rgba(28, 45, 236, 0.1);
    color: #1c2dec;
  }

  &.is-owned,
  &.is-public,
  &.is-clinic {
    background: rgba(95, 113, 137, 0.14);
    color: #44566c;
  }

  &.is-locked {
    background: rgba(161, 75, 75, 0.12);
    color: #a14b4b;
  }

  &.is-history {
    background: rgba(120, 129, 145, 0.14);
    color: #606b7a;
  }
}

.detail-link {
  border: 0;
  background: transparent;
  color: #1c2dec;
  font-size: 11px;
  font-weight: 700;
  white-space: nowrap;
  padding: 0;
}

.header {
  font-size: 17px;
  line-height: 1.2;
  font-weight: 800;
  color: #111a2d;
  word-break: break-word;
}

.header-cart {
  font-size: 18px;
  line-height: 1.24;
}

.subtitle {
  color: #54657e;
  font-size: 12px;
  line-height: 1.35;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.meta-row {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.meta-pill {
  padding: 4px 9px;
  font-size: 10px;
  font-weight: 600;
  color: #5c6f88;
  background: #f3f6fc;
  &.is-days-left {
    background: rgba(255, 153, 0, 0.14);
    color: #c26a00;
    font-weight: 800;
  }
}

.detail,
.lock-reason,
.clinic-code,
.expired {
  font-size: 11px;
  line-height: 1.4;
}

.detail {
  color: #66758b;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lock-reason {
  color: #a14b4b;
  word-break: break-word;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.clinic-code {
  color: #44566c;
  font-weight: 600;
}

.content-bottom {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
}

.expired {
  color: #878787;
  flex: 1;
}

.action-btn {
  min-width: 92px;
  padding: 10px 12px;
  border-radius: 999px;
  border: 0;
  font-size: 12px;
  font-weight: 800;
  color: var(--white-color);
  background: var(--secondary-color);
  flex-shrink: 0;
  box-shadow: 0 10px 22px rgba(28, 45, 236, 0.16);

  &.secondary {
    background: #5f7189;
    box-shadow: none;
  }

  &.free {
    background: #4cc764;
    box-shadow: 0 10px 22px rgba(76, 199, 100, 0.2);
  }

  &.locked {
    background: #c3c3c3;
    box-shadow: none;
    cursor: not-allowed;
  }
}

.check-wrapper {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid var(--success-color);
  margin: auto 2px auto 0;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  transition: all 0.3s ease;

  &.is-selected {
    background: var(--success-color);
  }
}
</style>
